import { useState } from 'react';
import { resolveCompanySidebarLogoUrl } from '@shared/schemas/company-branding';
import { AppBrandLogo } from '@/components/app-brand-logo';
import { useOrganizationAccess } from '@/features/organization/useOrganizationAccess';
import { authClient } from '@/lib/auth-client';
import { SITE_TITLE } from '@/lib/site-meta';
import { useThemeCustomizer } from '@/lib/theme-customizer';
import { cn } from '@/lib/utils';
import { useActiveCompanyBranding } from '../use-company-branding';

type SessionUserWithRole = {
  role?: string | null;
};

type CompanySidebarLogoProps = {
  compact?: boolean;
  className?: string;
};

export function CompanySidebarLogo({ compact = false, className }: CompanySidebarLogoProps) {
  const { effectiveMode } = useThemeCustomizer();
  const orgAccess = useOrganizationAccess();
  const sessionState = authClient.useSession();
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const role = ((sessionState.data?.user as SessionUserWithRole | undefined)?.role ?? 'user')
    .toLowerCase();
  // Le logo personnalisé ne remplace que le grand logo (sidebar déployée) ;
  // le symbole compact reste toujours celui de la plateforme.
  const brandingQuery = useActiveCompanyBranding(
    !compact && (orgAccess.canReadCompanyBranding || role === 'admin'),
  );
  const logoUrl = brandingQuery.data
    ? resolveCompanySidebarLogoUrl({
      logo: brandingQuery.data.logo,
      logoDark: brandingQuery.data.logoDark,
      theme: effectiveMode,
    })
    : null;
  const showCustomLogo = !compact && Boolean(logoUrl) && failedSrc !== logoUrl;

  if (showCustomLogo && logoUrl) {
    return (
      <img
        alt={SITE_TITLE}
        className={cn('h-8 w-full max-w-full object-contain object-left', className)}
        data-testid="company-sidebar-logo"
        onError={() => setFailedSrc(logoUrl)}
        src={logoUrl}
      />
    );
  }

  return <AppBrandLogo className={className} compact={compact} />;
}
